import React, { useState } from 'react';
import { Terminal, Cpu, Layers, ShieldCheck, Activity, CheckCircle2, Zap, ArrowRight } from 'lucide-react';
import { cn } from '../../utils/cn';

export const HeroTechConsole: React.FC<{ onOpenConsultation?: () => void }> = ({ onOpenConsultation }) => {
  const [activeTab, setActiveTab] = useState<'sandbox' | 'architecture' | 'roi'>('sandbox');
  const [activePillar, setActivePillar] = useState<'dev' | 'marketing' | 'leadgen' | 'admin'>('dev');

  return (
    <div className="relative w-full max-w-xl mx-auto">
      {/* Background Glowing Mesh Orbs */}
      <div className="absolute -top-12 -right-12 w-64 h-64 bg-sky-500/20 rounded-full blur-3xl pointer-events-none animate-pulse" />
      <div className="absolute -bottom-12 -left-12 w-64 h-64 bg-indigo-500/20 rounded-full blur-3xl pointer-events-none" />

      {/* Main Glassmorphic Console Shell */}
      <div className="relative z-10 bg-slate-950/85 border border-slate-800/90 rounded-3xl shadow-2xl shadow-sky-500/10 backdrop-blur-2xl overflow-hidden">
        {/* Console Header Bar */}
        <div className="px-5 py-3.5 bg-slate-900/90 border-b border-slate-800/80 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-rose-500/80 inline-block" />
            <span className="w-3 h-3 rounded-full bg-amber-500/80 inline-block" />
            <span className="w-3 h-3 rounded-full bg-emerald-500/80 inline-block" />
            <span className="text-xs font-mono text-slate-400 ml-2 flex items-center gap-1.5">
              <Terminal size={13} className="text-sky-400" /> technook-console.v2
            </span>
          </div>

          {/* Console Tab Switchers */}
          <div className="flex items-center gap-1 p-1 bg-slate-950/90 border border-slate-800/80 rounded-xl text-[11px]">
            <button
              onClick={() => setActiveTab('sandbox')}
              className={cn('px-2.5 py-1 rounded-lg transition-colors font-medium', activeTab === 'sandbox' ? 'bg-sky-500/20 text-sky-400' : 'text-slate-400 hover:text-slate-200')}
            >
              Sandbox
            </button>
            <button
              onClick={() => setActiveTab('architecture')}
              className={cn('px-2.5 py-1 rounded-lg transition-colors font-medium', activeTab === 'architecture' ? 'bg-sky-500/20 text-sky-400' : 'text-slate-400 hover:text-slate-200')}
            >
              Architecture
            </button>
            <button
              onClick={() => setActiveTab('roi')}
              className={cn('px-2.5 py-1 rounded-lg transition-colors font-medium', activeTab === 'roi' ? 'bg-sky-500/20 text-sky-400' : 'text-slate-400 hover:text-slate-200')}
            >
              ROI Matrix
            </button>
          </div>
        </div>

        {/* Console Content Panel */}
        <div className="p-6">
          {activeTab === 'sandbox' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between border-b border-slate-800/80 pb-3">
                <span className="text-xs font-semibold text-slate-300 uppercase tracking-wider">Solution Ecosystem</span>
                <span className="text-xs text-emerald-400 font-mono flex items-center gap-1">
                  <Activity size={13} className="animate-pulse" /> Latency: &lt;12ms
                </span>
              </div>

              {/* Pillar Selector Buttons */}
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => setActivePillar('dev')}
                  className={cn('p-3 rounded-xl border text-left transition-all text-xs', activePillar === 'dev' ? 'bg-sky-500/10 border-sky-500/50 text-white' : 'bg-slate-900/40 border-slate-800 text-slate-400 hover:text-slate-200')}
                >
                  <div className="font-bold flex items-center gap-1.5 mb-1 text-sky-400">
                    <Cpu size={14} /> Dev Services
                  </div>
                  <p className="text-[11px] text-slate-400">Web, Mobile & APIs</p>
                </button>
                <button
                  onClick={() => setActivePillar('marketing')}
                  className={cn('p-3 rounded-xl border text-left transition-all text-xs', activePillar === 'marketing' ? 'bg-indigo-500/10 border-indigo-500/50 text-white' : 'bg-slate-900/40 border-slate-800 text-slate-400 hover:text-slate-200')}
                >
                  <div className="font-bold flex items-center gap-1.5 mb-1 text-indigo-400">
                    <Zap size={14} /> Brand & Media
                  </div>
                  <p className="text-[11px] text-slate-400">3D, Video & Assets</p>
                </button>
              </div>

              {/* Live Preview Box */}
              <div className="p-4 bg-slate-900/90 border border-slate-800 rounded-2xl space-y-2.5 font-mono text-xs text-slate-300">
                {activePillar === 'dev' && (
                  <>
                    <div className="flex items-center justify-between text-sky-400 font-semibold border-b border-slate-800 pb-1.5">
                      <span>STATUS: READY_FOR_DEPLOYMENT</span>
                      <span>v2.4.0</span>
                    </div>
                    <p className="text-slate-400">{'// React 18 • TypeScript • Node.js • Mobile iOS/Android'}</p>
                    <div className="flex flex-wrap gap-1.5 pt-1">
                      <span className="px-2 py-0.5 bg-sky-500/20 text-sky-300 rounded text-[10px]">Restful APIs</span>
                      <span className="px-2 py-0.5 bg-indigo-500/20 text-indigo-300 rounded text-[10px]">GraphQL</span>
                      <span className="px-2 py-0.5 bg-emerald-500/20 text-emerald-300 rounded text-[10px]">AWS Cloud</span>
                    </div>
                  </>
                )}
                {activePillar === 'marketing' && (
                  <>
                    <div className="flex items-center justify-between text-indigo-400 font-semibold border-b border-slate-800 pb-1.5">
                      <span>MEDIA: HIGH_CONVERSION_BRANDING</span>
                      <span>4K Ready</span>
                    </div>
                    <p className="text-slate-400">{'// 3D Architectural Rendering • Motion Graphics • CRM'}</p>
                    <div className="flex flex-wrap gap-1.5 pt-1">
                      <span className="px-2 py-0.5 bg-indigo-500/20 text-indigo-300 rounded text-[10px]">Brand Identity</span>
                      <span className="px-2 py-0.5 bg-purple-500/20 text-purple-300 rounded text-[10px]">Blender 3D</span>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {activeTab === 'architecture' && (
            <div className="space-y-3 font-mono text-xs">
              <div className="p-3 bg-slate-900/80 border border-slate-800 rounded-xl space-y-1.5 text-slate-300">
                <div className="text-emerald-400 font-semibold flex items-center gap-1.5">
                  <CheckCircle2 size={14} /> Client Web & Mobile Apps
                </div>
                <p className="text-slate-400 text-[11px] pl-5">├── React, Next.js & Mobile SDKs</p>
                <p className="text-slate-400 text-[11px] pl-5">└── Secure JWT & OAuth Authentication</p>
              </div>
              <div className="p-3 bg-slate-900/80 border border-slate-800 rounded-xl space-y-1.5 text-slate-300">
                <div className="text-sky-400 font-semibold flex items-center gap-1.5">
                  <Layers size={14} /> Technook Microservices API
                </div>
                <p className="text-slate-400 text-[11px] pl-5">├── Node.js / Python Backend Engine</p>
                <p className="text-slate-400 text-[11px] pl-5">└── PostgreSQL & Distributed Redis Cache</p>
              </div>
            </div>
          )}

          {activeTab === 'roi' && (
            <div className="space-y-4">
              <div className="p-4 bg-gradient-to-r from-sky-500/10 to-indigo-500/10 border border-sky-500/30 rounded-2xl">
                <h5 className="text-xs font-bold text-white mb-2 flex items-center justify-between">
                  <span>Measured Business Multiplier</span>
                  <span className="text-emerald-400">+340% ROI</span>
                </h5>
                <div className="space-y-2 text-xs text-slate-300">
                  <div className="flex justify-between">
                    <span className="text-slate-400">Development Velocity:</span>
                    <span className="font-bold text-sky-400">4.5x Faster</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Operational Cost Overhead:</span>
                    <span className="font-bold text-emerald-400">-65% Reduction</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Action Trigger inside Console */}
          <div className="mt-5 pt-4 border-t border-slate-800/80 flex items-center justify-between">
            <span className="text-xs text-slate-400 flex items-center gap-1">
              <ShieldCheck size={14} className="text-emerald-400" /> Parañaque HQ Enterprise Ready
            </span>
            <button
              onClick={onOpenConsultation}
              className="px-3.5 py-1.5 rounded-xl bg-gradient-to-r from-sky-500 to-indigo-600 hover:from-sky-400 hover:to-indigo-500 text-white text-xs font-semibold shadow-lg shadow-sky-500/20 flex items-center gap-1 transition-all"
            >
              <span>Build Scope</span> <ArrowRight size={13} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
